import { Users, DollarSign, Clock } from "lucide-react";
import type { UpworkJob } from "../api/UpworkJobsApi";
import TimeAgoChip from "shared/TimeAgoChip";
import { daysAgo, isStale, isVeryStale } from "./shared";

interface Props {
  job: UpworkJob;
  onClick: () => void;
}

export default function JobCard({ job, onClick }: Props) {
  return (
    <div
      onClick={onClick}
      className="bg-surface/60 border border-border rounded-xl p-4 cursor-pointer hover:border-muted-foreground transition-all select-none"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2">{job.title}</h3>
        {!job.is_active && (
          <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter">
            Inactive
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-primary/10 text-primary border-primary/20 font-inter">
          {job.search_profile}
        </span>
        {job.experience_level && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter">
            {job.experience_level}
          </span>
        )}
        {job.job_type && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter">
            {job.job_type}
          </span>
        )}
        {job.amount_display_value && (
          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/20 font-inter">
            <DollarSign className="w-2.5 h-2.5" />
            {job.amount_display_value}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2">
        {job.total_applicants != null && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
            <Users className="w-3 h-3" />
            {job.total_applicants} applicants
          </span>
        )}
        {job.published_date_time && <TimeAgoChip label={daysAgo(job.published_date_time)} title={new Date(job.published_date_time).toLocaleString()} />}
        {isStale(job.discovered_at) && (
          <span
            title={`Cached ${daysAgo(job.discovered_at).toLowerCase()} — details may be outdated`}
            className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter cursor-help ${
              isVeryStale(job.discovered_at)
                ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                : "bg-muted/40 text-muted-foreground border-border"
            }`}
          >
            <Clock className="w-2.5 h-2.5" />
            {isVeryStale(job.discovered_at) ? "Stale — consider cleanup" : "May be outdated"}
          </span>
        )}
      </div>
    </div>
  );
}
